<template>
  <div class="pt-safe bg-topbar-background border-b border-content-background">
    <div class="px-2 sm:px-4 py-2 sm:py-3 flex text-n-0 items-center h-14">
      <div class="flex-1 flex items-center shrink min-w-0">
        <div
          v-if="showBack"
          class="h-7 w-7 flex items-center m-2 cursor-pointer"
          @click="$emit('back')"
        >
          <l-icon name="lucide/arrow-left" size="medium" />
        </div>
        <div v-else class="shrink-0 flex m-2 cursor-pointer" @click="goHome()">
          <img src="/icon.png" class="h-7 w-7" />
        </div>
        <router-link
          v-if="currentEmployee && listEmployees.length > 1"
          :to="{ name: 'employees' }"
          class="ml-4 items-center min-w-0 hidden sm:flex"
        >
          <l-icon name="lucide/user" class="mr-2 opacity-60" size="medium" />
          <div class="text-ellipsis min-w-0 text-nowrap overflow-hidden body-3">
            {{ currentEmployee.name }}
          </div>
        </router-link>
        <div
          v-if="tills.length > 1 && selectedCashTill"
          class="hidden sm:flex items-center min-w-0"
        >
          <div class="text-ellipsis min-w-0 text-nowrap overflow-hidden body-3 text-n-300">
            <span class="px-1">·</span>{{ selectedCashTill.name }}
          </div>
        </div>
      </div>
      <div class="flex justify-center">
        <slot name="center" />
      </div>
      <div class="flex-1 flex justify-end text-n-0 items-center sm:gap-2">
        <l-button
          v-if="!isConnected"
          icon="lucide/wifi-off"
          size="small"
          color="red"
          class="rounded-full"
        />
        <slot name="right">
          <div class="px-2 body-3 hidden sm:flex text-n-200">
            {{ format(time, 'HH:mm') }}
          </div>
          <div v-if="products.pos" class="justify-end p-3 hidden sm:flex">
            <router-link :to="{ name: 'search' }">
              <l-icon name="lucide/search" size="medium" />
            </router-link>
          </div>
          <div class="relative cursor-pointer p-3" @click="showNotifications = true">
            <div v-if="notificationsAmount > 0" class="absolute z-10 right-2 top-1">
              <l-badge
                class="cursor-pointer"
                mode="alert"
                type="small"
                :value="notificationsAmount"
              />
            </div>
            <l-icon name="lucide/bell" size="medium" />
          </div>
          <div class="p-3 cursor-pointer" @click="showMenu = true">
            <l-icon name="lucide/menu" size="medium" />
          </div>
        </slot>
      </div>
    </div>

    <side-menu :open="showMenu" @close="showMenu = false" />
    <notifications :open="showNotifications" @close="showNotifications = false" />
  </div>
</template>

<script setup lang="ts">
import { useNow } from '@vueuse/core'
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { LBadge, LButton, LIcon } from '@last/core-ui/paprika'

import SideMenu from '@/components/SideMenu.vue'
import { useHome } from '@/composables/useHome'
import { useNotifications } from '@/composables/useNotifications'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useStatusStore } from '@/store/status'
import { useTillStore } from '@/store/till'

import Notifications from './Notifications.vue'

type Props = {
  showBack?: boolean
}

withDefaults(defineProps<Props>(), {
  showBack: false
})

defineEmits<{
  back: []
}>()

const time = useNow({
  interval: 1_000
})

const authStore = useAuthStore()
const tillStore = useTillStore()
const configStore = useConfigStore()
const statusStore = useStatusStore()

const { currentEmployee, listEmployees } = storeToRefs(authStore)
const { selectedCashTill } = storeToRefs(tillStore)
const { tills, products } = storeToRefs(configStore)
const { isConnected } = storeToRefs(statusStore)

const { amount: notificationsAmount } = useNotifications()
const { resetToHome } = useHome()
const router = useRouter()

function goHome() {
  resetToHome()
  router.push({ name: 'pos' })
}

const showMenu = ref(false)
const showNotifications = ref(false)
</script>
